import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Alert as RNAlert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';

// Web-compatible alert wrapper
const Alert = {
  alert: (title: string, message?: string, buttons?: any[]) => {
    if (Platform.OS === 'web') {
      // Use browser's native alert for web
      const alertMessage = message ? `${title}\n\n${message}` : title;
      window.alert(alertMessage);
      // Call the first button's onPress if it exists (usually 'OK')
      if (buttons && buttons[0] && buttons[0].onPress) {
        buttons[0].onPress();
      }
    } else {
      // Use React Native's Alert for mobile
      RNAlert.alert(title, message, buttons);
    }
  }
};
import {
  Text,
  Card,
  Button,
  TextInput,
  IconButton,
  useTheme,
  Appbar,
  Divider,
  Chip,
} from 'react-native-paper';
import { useSelector, useDispatch } from 'react-redux';
import { useRoute, useNavigation, RouteProp, NavigationProp } from '@react-navigation/native';
import { RootState, AppDispatch } from '../store';
import {
  generateVendorEmails,
  fetchVendorEmails,
  updateVendorEmailStatus,
  updateVendorEmailContent,
  sendVendorEmail,
} from '../store/slices/vendorEmailSlice';
import { ReviewStackParamList, ResultsStackParamList } from '../navigation/AppNavigator';
import { LoadingScreen } from '../components/LoadingScreen';

// Support navigation from both stacks
type VendorEmailsScreenRouteProp = RouteProp<ReviewStackParamList | ResultsStackParamList, 'VendorEmails'>;
type VendorEmailsScreenNavigationProp = NavigationProp<ReviewStackParamList | ResultsStackParamList, 'VendorEmails'>;

const VendorEmailsScreen = () => {
  const theme = useTheme();
  const route = useRoute<VendorEmailsScreenRouteProp>();
  const navigation = useNavigation<VendorEmailsScreenNavigationProp>();
  const dispatch = useDispatch<AppDispatch>();
  
  const { reviewId } = route.params;
  const reviewIdNum = Number(reviewId);

  // Redux state
  const vendorEmailsState = useSelector((state: RootState) => state.vendorEmails);
  const vendorEmails = vendorEmailsState?.emails?.[reviewIdNum] || [];
  const isGeneratingEmails = vendorEmailsState?.isGenerating?.[reviewIdNum] || false;
  const isLoadingEmails = vendorEmailsState?.isLoading?.[reviewIdNum] || false;
  
  // Local state for feedback
  const [feedbackText, setFeedbackText] = useState<Record<number, string>>({});
  const [regeneratingIds, setRegeneratingIds] = useState<Set<number>>(new Set());
  const [sendingIds, setSendingIds] = useState<Set<number>>(new Set());
  
  // Local state for editing
  const [editingIds, setEditingIds] = useState<Set<number>>(new Set());
  const [editedSubjects, setEditedSubjects] = useState<Record<number, string>>({});
  const [editedContents, setEditedContents] = useState<Record<number, string>>({});
  
  // Local state for vendor selection - vendorTypeId -> Set of selected vendor IDs
  const [selectedVendors, setSelectedVendors] = useState<Record<number, Set<number>>>({});

  // Local state for async job polling
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<{ completed: number; total: number; errors: any[] } | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    // Always fetch emails when screen loads to get latest data including vendors
    const loadEmails = async () => {
      try {
        const result = await dispatch(fetchVendorEmails(reviewIdNum)).unwrap();
        
        // If no emails exist, generate them
        if (!result.emails || result.emails.length === 0) {
          handleGenerateEmails();
        }
      } catch (error) {
        console.error('Failed to fetch vendor emails:', error);
        // If fetch fails, try to generate
        handleGenerateEmails();
      }
    };
    
    loadEmails();
  }, []);
  
  // Initialize selected vendors when emails are loaded
  useEffect(() => {
    if (vendorEmails.length > 0) {
      const newSelectedVendors: Record<number, Set<number>> = {};
      vendorEmails.forEach(email => {
        if (email.vendors && email.vendors.length > 0) {
          // Default: all vendors selected
          newSelectedVendors[email.vendorTypeId] = new Set(email.vendors.map(v => v.id));
        }
      });
      setSelectedVendors(prevState => {
        // Only update if we don't have selections for these vendor types yet
        const merged = { ...prevState };
        Object.keys(newSelectedVendors).forEach(key => {
          const vendorTypeId = parseInt(key);
          if (!prevState[vendorTypeId]) {
            merged[vendorTypeId] = newSelectedVendors[vendorTypeId];
          }
        });
        return merged;
      });
    }
  }, [vendorEmails]);

  // Polling effect for job status
  useEffect(() => {
    if (!isPolling || !currentJobId) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const { reviewService } = await import('../services/reviewService');
        const statusResponse = await reviewService.getVendorEmailJobStatus(reviewIdNum, currentJobId);

        setJobStatus(statusResponse.status);
        setJobProgress(statusResponse.progress);

        // Stop polling when job is complete
        if (statusResponse.status === 'success' || statusResponse.status === 'error') {
          setIsPolling(false);
          setCurrentJobId(null);

          // Refresh emails
          await dispatch(fetchVendorEmails(reviewIdNum)).unwrap();

          // Show completion message
          if (statusResponse.status === 'success') {
            const emailCount = statusResponse.emailCount || 0;
            const errorCount = statusResponse.progress?.errors?.length || 0;

            if (errorCount > 0) {
              Alert.alert('Partial Success', `Generated ${emailCount} emails. ${errorCount} failed.`);
            } else {
              Alert.alert('Success', `Generated ${emailCount} vendor emails successfully!`);
            }
          } else if (statusResponse.status === 'error') {
            Alert.alert('Error', statusResponse.errorText || 'Failed to generate vendor emails');
          }
        }
      } catch (error) {
        console.error('[VendorEmailsScreen] Failed to poll job status:', error);
        // Don't stop polling on error - just log it
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [isPolling, currentJobId, reviewIdNum, dispatch]);

  const handleGenerateEmails = async () => {
    try {
      // Call the generate endpoint which now returns 202 with jobId
      const { reviewService } = await import('../services/reviewService');
      const response = await reviewService.generateVendorEmails(reviewIdNum);

      // Extract jobId from response and start polling
      if (response.jobId) {
        setCurrentJobId(response.jobId);
        setJobStatus('pending');
        setIsPolling(true);
      }
    } catch (error) {
      console.error('Failed to generate vendor emails:', error);
      Alert.alert('Error', 'Failed to start vendor email generation');
    }
  };
  
  const handleRegenerateEmail = async (vendorTypeId: number) => {
    const feedback = feedbackText[vendorTypeId];
    if (!feedback?.trim()) {
      Alert.alert('Error', 'Please provide feedback for regeneration');
      return;
    }

    try {
      // Add to regenerating set
      setRegeneratingIds(prev => new Set(prev).add(vendorTypeId));

      // Call the generate endpoint with feedback (async pattern)
      const { reviewService } = await import('../services/reviewService');
      const response = await reviewService.generateVendorEmails(reviewIdNum, { [vendorTypeId]: feedback });

      // Extract jobId and start polling
      if (response.jobId) {
        setCurrentJobId(response.jobId);
        setJobStatus('pending');
        setIsPolling(true);
      }

      // Clear feedback after starting regeneration
      setFeedbackText(prev => ({ ...prev, [vendorTypeId]: '' }));
    } catch (error) {
      console.error('Failed to regenerate email:', error);
      Alert.alert('Error', 'Failed to start email regeneration');
    } finally {
      // Remove from regenerating set
      setRegeneratingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(vendorTypeId);
        return newSet;
      });
    }
  };
  
  const handleSendEmail = async (vendorTypeId: number) => {
    try {
      // Get selected vendor IDs (always required)
      const selectedVendorSet = selectedVendors[vendorTypeId];

      if (!selectedVendorSet || selectedVendorSet.size === 0) {
        // No vendors selected
        Alert.alert(
          'No Vendors Selected',
          'Please select at least one vendor to send the email to, or click "Dismiss" if you don\'t want to send this email.',
          [{ text: 'OK' }]
        );
        return;
      }
      
      // Get the vendor IDs as an array
      const vendorIds = Array.from(selectedVendorSet);
      
      setSendingIds(prev => new Set(prev).add(vendorTypeId));
      
      const result = await dispatch(sendVendorEmail({
        reviewId: reviewIdNum,
        vendorTypeId,
        vendorIds,
      })).unwrap();
      
      const vendorCount = vendorIds ? vendorIds.length : 0;
      Alert.alert('Success', vendorCount > 0 ? `Email sent to ${vendorCount} vendor(s)!` : 'Email sent successfully!');
      
      if (result.reviewCompleted) {
        Alert.alert('Review Complete', 'All vendor emails have been processed. Your review is now complete!');
      }
    } catch (error: any) {
      console.error('Failed to send email:', error);
      
      // Check if it's a specific error about missing email addresses
      if (error?.error === 'MISSING_EMAIL_ADDRESSES' || 
          (error?.message && error.message.includes('email addresses configured'))) {
        Alert.alert(
          'Email Addresses Missing', 
          error.message || 'The selected vendors do not have email addresses configured. Please update vendor information and try again.'
        );
      } else {
        Alert.alert('Error', error?.message || 'Failed to send email. Please try again.');
      }
    } finally {
      setSendingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(vendorTypeId);
        return newSet;
      });
    }
  };

  const handleDismissEmail = async (vendorTypeId: number) => {
    try {
      await dispatch(updateVendorEmailStatus({
        reviewId: reviewIdNum,
        vendorTypeId,
        status: 'dismissed',
      })).unwrap();
      
      Alert.alert('Success', 'Email dismissed successfully');
    } catch (error) {
      console.error('Failed to dismiss email:', error);
      Alert.alert('Error', 'Failed to dismiss email');
    }
  };
  
  const toggleVendorSelection = (vendorTypeId: number, vendorId: number) => {
    setSelectedVendors(prev => {
      const newSelection = new Set(prev[vendorTypeId] || []);
      if (newSelection.has(vendorId)) {
        newSelection.delete(vendorId);
      } else {
        newSelection.add(vendorId);
      }
      return { ...prev, [vendorTypeId]: newSelection };
    });
  };
  
  const selectAllVendors = (vendorTypeId: number, vendors: any[]) => {
    setSelectedVendors(prev => ({
      ...prev,
      [vendorTypeId]: new Set(vendors.map(v => v.id))
    }));
  };
  
  const deselectAllVendors = (vendorTypeId: number) => {
    setSelectedVendors(prev => ({
      ...prev,
      [vendorTypeId]: new Set()
    }));
  };
  
  const handleStartEdit = (vendorTypeId: number, email: any) => {
    setEditingIds(prev => new Set(prev).add(vendorTypeId));
    setEditedSubjects(prev => ({ ...prev, [vendorTypeId]: email.subject }));
    setEditedContents(prev => ({ ...prev, [vendorTypeId]: email.content }));
  };
  
  const handleCancelEdit = (vendorTypeId: number) => {
    setEditingIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(vendorTypeId);
      return newSet;
    });
    // Clear edited values
    setEditedSubjects(prev => {
      const newSubjects = { ...prev };
      delete newSubjects[vendorTypeId];
      return newSubjects;
    });
    setEditedContents(prev => {
      const newContents = { ...prev };
      delete newContents[vendorTypeId];
      return newContents;
    });
  };
  
  const handleSaveEdit = async (vendorTypeId: number) => {
    const subject = editedSubjects[vendorTypeId];
    const content = editedContents[vendorTypeId];
    
    if (!subject?.trim() || !content?.trim()) {
      Alert.alert('Error', 'Subject and content cannot be empty');
      return;
    }
    
    try {
      await dispatch(updateVendorEmailContent({
        reviewId: reviewIdNum,
        vendorTypeId,
        subject,
        content,
      })).unwrap();
      
      // Clear editing state
      handleCancelEdit(vendorTypeId);
      
      Alert.alert('Success', 'Email content updated successfully');
    } catch (error) {
      console.error('Failed to update email content:', error);
      Alert.alert('Error', 'Failed to update email content');
    }
  };
  
  if (isLoadingEmails || (isGeneratingEmails && vendorEmails.length === 0)) {
    return (
      <LoadingScreen 
        testID="vendor-emails-screen"
        message="Generating Vendor Emails..." 
        subtitle="Please wait while we create personalized emails for each vendor type"
      />
    );
  }
  
  return (
    <View testID="vendor-emails-screen" style={styles.container}>
      <KeyboardAvoidingView 
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          testID="vendor-emails-scroll"
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Progress Banner */}
          {isPolling && (
            <Card style={styles.progressCard}>
              <Card.Content>
                <View style={styles.progressHeader}>
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                  <Text style={styles.progressTitle}>Generating Vendor Emails...</Text>
                </View>
                {jobProgress && jobProgress.total > 0 && (
                  <Text style={styles.progressText}>
                    {jobProgress.completed} of {jobProgress.total} completed
                  </Text>
                )}
                {jobProgress && jobProgress.errors && jobProgress.errors.length > 0 && (
                  <Text style={styles.errorText}>
                    {jobProgress.errors.length} error(s) occurred
                  </Text>
                )}
              </Card.Content>
            </Card>
          )}

          {vendorEmails.length === 0 && !isPolling ? (
            <Card style={styles.emptyCard}>
              <Card.Content>
                <Text style={styles.emptyText}>
                  {jobStatus === 'error' ? 'Email generation failed. Please try again.' : 'No vendor emails generated yet.'}
                </Text>
                <Button
                  testID="generate-emails-button"
                  mode="contained"
                  onPress={handleGenerateEmails}
                  style={styles.generateButton}
                  loading={isGeneratingEmails || isPolling}
                  disabled={isGeneratingEmails || isPolling}
                >
                  {jobStatus === 'error' ? 'Retry Generation' : 'Generate Emails'}
                </Button>
              </Card.Content>
            </Card>
          ) : vendorEmails.length > 0 ? (
            vendorEmails.map((email, index) => (
              <Card key={email.vendorTypeId} style={styles.emailCard}>
                <Card.Content>
                  <View style={styles.emailHeader}>
                    <Text style={styles.vendorType}>{email.vendorTypeName}</Text>
                    <View style={styles.headerRight}>
                      {email.status !== 'draft' && (
                        <View style={[styles.statusBadge, { backgroundColor: 
                          email.status === 'sent' ? theme.colors.primary : theme.colors.error 
                        }]}>
                          <Text style={styles.statusText}>{email.status}</Text>
                        </View>
                      )}
                      {email.status === 'draft' && !editingIds.has(email.vendorTypeId) && (
                        <IconButton
                          icon="pencil"
                          size={20}
                          onPress={() => handleStartEdit(email.vendorTypeId, email)}
                        />
                      )}
                    </View>
                  </View>
                  
                  <Divider style={styles.divider} />
                  
                  {editingIds.has(email.vendorTypeId) ? (
                    <>
                      <TextInput
                        label="Subject"
                        value={editedSubjects[email.vendorTypeId] || ''}
                        onChangeText={(text) => setEditedSubjects(prev => ({
                          ...prev,
                          [email.vendorTypeId]: text
                        }))}
                        mode="outlined"
                        style={styles.editInput}
                      />
                      
                      <TextInput
                        label="Content"
                        value={editedContents[email.vendorTypeId] || ''}
                        onChangeText={(text) => setEditedContents(prev => ({
                          ...prev,
                          [email.vendorTypeId]: text
                        }))}
                        mode="outlined"
                        multiline
                        numberOfLines={10}
                        style={[styles.editInput, styles.contentEditInput]}
                      />
                      
                      <View style={styles.editActions}>
                        <Button
                          mode="outlined"
                          onPress={() => handleCancelEdit(email.vendorTypeId)}
                          style={styles.editButton}
                        >
                          Cancel
                        </Button>
                        <Button
                          mode="contained"
                          onPress={() => handleSaveEdit(email.vendorTypeId)}
                          style={styles.editButton}
                        >
                          Save
                        </Button>
                      </View>
                    </>
                  ) : (
                    <>
                      <Text style={styles.subjectLabel}>Subject:</Text>
                      <Text style={styles.subject}>{email.subject}</Text>
                      
                      <Text style={styles.contentLabel}>Content:</Text>
                      <View style={styles.contentContainer}>
                        {regeneratingIds.has(email.vendorTypeId) && (
                          <View style={styles.loadingOverlay}>
                            <ActivityIndicator size="large" color={theme.colors.primary} />
                          </View>
                        )}
                        <Text style={[
                          styles.content,
                          regeneratingIds.has(email.vendorTypeId) && styles.contentLoading
                        ]}>
                          {email.content}
                        </Text>
                      </View>
                    </>
                  )}
                  
                  {email.status === 'draft' && !editingIds.has(email.vendorTypeId) && (
                    <>
                      <View style={styles.feedbackContainer}>
                        <TextInput
                          label="Feedback for regeneration"
                          value={feedbackText[email.vendorTypeId] || ''}
                          onChangeText={(text) => setFeedbackText(prev => ({
                            ...prev,
                            [email.vendorTypeId]: text
                          }))}
                          mode="outlined"
                          multiline
                          numberOfLines={3}
                          style={styles.feedbackInput}
                          placeholder="e.g., make it more formal, add urgency, change tone..."
                          disabled={regeneratingIds.has(email.vendorTypeId)}
                        />
                        <Button
                          mode="contained"
                          onPress={() => handleRegenerateEmail(email.vendorTypeId)}
                          style={styles.regenerateButton}
                          icon="refresh"
                          loading={regeneratingIds.has(email.vendorTypeId)}
                          disabled={!feedbackText[email.vendorTypeId]?.trim() || regeneratingIds.has(email.vendorTypeId)}
                        >
                          Regenerate
                        </Button>
                      </View>
                      
                      {/* Vendor selection UI */}
                      {email.vendors && email.vendors.length > 0 && (
                        <>
                          <Divider style={styles.divider} />
                          <View style={styles.vendorSelectionContainer}>
                            <View style={styles.vendorSelectionHeader}>
                              <Text style={styles.vendorSelectionTitle}>Select vendors to send to:</Text>
                              <View style={styles.vendorSelectionButtons}>
                                <Button
                                  mode="text"
                                  onPress={() => selectAllVendors(email.vendorTypeId, email.vendors || [])}
                                  compact
                                  disabled={sendingIds.has(email.vendorTypeId)}
                                  labelStyle={styles.selectButtonLabel}
                                >
                                  Select All
                                </Button>
                                <Button
                                  mode="text"
                                  onPress={() => deselectAllVendors(email.vendorTypeId)}
                                  compact
                                  disabled={sendingIds.has(email.vendorTypeId)}
                                  labelStyle={styles.selectButtonLabel}
                                >
                                  Clear
                                </Button>
                              </View>
                            </View>
                            <View style={styles.vendorChipsContainer}>
                              {email.vendors.map(vendor => (
                                <Chip
                                  key={vendor.id}
                                  selected={selectedVendors[email.vendorTypeId]?.has(vendor.id) || false}
                                  onPress={() => toggleVendorSelection(email.vendorTypeId, vendor.id)}
                                  disabled={sendingIds.has(email.vendorTypeId)}
                                  style={[
                                    styles.vendorChip,
                                    selectedVendors[email.vendorTypeId]?.has(vendor.id) && styles.vendorChipSelected
                                  ]}
                                  textStyle={styles.vendorChipText}
                                  mode="outlined"
                                >
                                  {vendor.name}
                                </Chip>
                              ))}
                            </View>
                            {selectedVendors[email.vendorTypeId] && (
                              <Text style={styles.vendorCount}>
                                {selectedVendors[email.vendorTypeId].size > 0 
                                  ? `Sending to ${selectedVendors[email.vendorTypeId].size} of ${email.vendors.length} vendors`
                                  : 'No vendors selected'}
                              </Text>
                            )}
                          </View>
                        </>
                      )}
                      
                      <View style={styles.actionButtons}>
                        <Button
                          mode="outlined"
                          onPress={() => handleDismissEmail(email.vendorTypeId)}
                          style={styles.actionButton}
                          icon="close"
                          disabled={sendingIds.has(email.vendorTypeId)}
                        >
                          Dismiss
                        </Button>
                        <Button
                          mode="contained"
                          onPress={() => handleSendEmail(email.vendorTypeId)}
                          style={styles.actionButton}
                          icon="send"
                          loading={sendingIds.has(email.vendorTypeId)}
                          disabled={sendingIds.has(email.vendorTypeId)}
                        >
                          Send Email
                        </Button>
                      </View>
                    </>
                  )}
                </Card.Content>
              </Card>
            ))
          ) : null}
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  emptyCard: {
    marginTop: 32,
  },
  emptyText: {
    textAlign: 'center',
    fontSize: 16,
    marginBottom: 16,
    color: '#666',
  },
  generateButton: {
    alignSelf: 'center',
  },
  progressCard: {
    marginBottom: 16,
    backgroundColor: '#e3f2fd',
    elevation: 2,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 12,
  },
  progressText: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  errorText: {
    fontSize: 14,
    color: '#d32f2f',
    marginTop: 4,
    fontWeight: '500',
  },
  emailCard: {
    marginBottom: 16,
    elevation: 2,
  },
  emailHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  vendorType: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  divider: {
    marginVertical: 12,
  },
  subjectLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 4,
    color: '#333',
  },
  subject: {
    fontSize: 14,
    marginBottom: 12,
    color: '#666',
  },
  contentLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 4,
    color: '#333',
  },
  contentContainer: {
    position: 'relative',
    marginBottom: 16,
  },
  content: {
    fontSize: 14,
    lineHeight: 20,
    color: '#444',
    backgroundColor: '#f9f9f9',
    padding: 12,
    borderRadius: 8,
  },
  contentLoading: {
    opacity: 0.5,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    zIndex: 1,
  },
  feedbackContainer: {
    marginBottom: 16,
  },
  feedbackInput: {
    marginBottom: 8,
  },
  regenerateButton: {
    alignSelf: 'flex-start',
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  actionButton: {
    flex: 1,
  },
  editInput: {
    marginBottom: 12,
  },
  contentEditInput: {
    minHeight: 200,
  },
  editActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
  },
  editButton: {
    minWidth: 100,
  },
  vendorSelectionContainer: {
    marginBottom: 16,
  },
  vendorSelectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  vendorSelectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  vendorSelectionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  selectButtonLabel: {
    fontSize: 12,
  },
  vendorChipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  vendorChip: {
    marginBottom: 4,
  },
  vendorChipSelected: {
    backgroundColor: '#e3f2fd',
  },
  vendorChipText: {
    fontSize: 13,
  },
  vendorCount: {
    fontSize: 12,
    color: '#666',
    marginTop: 8,
    fontStyle: 'italic',
  },
});

export default VendorEmailsScreen;
